// client/src/dnd/emitter_tool/runtime/RuntimeBridge.js
import * as PIXI from "pixi.js";
import {
  loadAtlas as loadEmitterAtlas,
  getAtlas as getEmitterAtlas,
} from "../../emitter_tool/spritesheetAtlas";
import unifiedRuntimeModule from "./UnifiedRuntime";

const createUnifiedEmitterRuntime =
  unifiedRuntimeModule.createUnifiedEmitterRuntime ||
  (unifiedRuntimeModule.default &&
    (unifiedRuntimeModule.default.createUnifiedEmitterRuntime ||
      unifiedRuntimeModule.default)) ||
  null;

if (!createUnifiedEmitterRuntime) {
  console.error(
    "[RuntimeBridge] Could not resolve createUnifiedEmitterRuntime from ./UnifiedRuntime."
  );
}

// ─────────────────────────────────────────────────────────────
// Feed VTT / Geoman walls into the emitter CollisionManager
// ─────────────────────────────────────────────────────────────
function broadcastWallsFromEnv(env) {
  if (typeof window === "undefined") return;
  if (!env || typeof env !== "object") return;

  let polygons = null;
  let space = "image";
  let dpr = 1;

  if (env.emitterWalls && typeof env.emitterWalls === "object") {
    const raw = env.emitterWalls;
    const polysLikely =
      raw.polygons ||
      raw.polys ||
      (Array.isArray(raw) ? raw : null);

    if (Array.isArray(polysLikely) && polysLikely.length) {
      polygons = polysLikely;
      space = raw.space || env.wallSpace || "image";
      dpr =
        Number.isFinite(raw.dpr) && raw.dpr > 0
          ? raw.dpr
          : Number.isFinite(env.wallDpr) && env.wallDpr > 0
          ? env.wallDpr
          : 1;
    }
  } else if (Array.isArray(env.wallPolygons)) {
    const polyArr = env.wallPolygons
      .filter(Array.isArray)
      .map((poly) =>
        poly
          .map((p) => {
            if (!p) return null;
            if (Array.isArray(p) && p.length >= 2) {
              const [x, y] = p;
              return Number.isFinite(x) && Number.isFinite(y)
                ? { x, y }
                : null;
            }
            const x = Number(p.x);
            const y = Number(p.y);
            return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
          })
          .filter(Boolean)
      )
      .filter((poly) => poly.length >= 2);

    if (polyArr.length) {
      polygons = polyArr;
      space = env.wallSpace || "image";
      dpr =
        Number.isFinite(env.wallDpr) && env.wallDpr > 0 ? env.wallDpr : 1;
    }
  }

  if (!polygons || !polygons.length) return;

  const payload = { polygons, space, dpr };
  window.__EmitterWallsLast = payload;

  try {
    window.dispatchEvent(
      new CustomEvent("EmitterTool:WallsUpdated", {
        detail: {
          polys: polygons,
          space,
          dpr,
        },
      })
    );
  } catch {}
}

export function createEmitterRuntime(arg) {
  let opts = {};
  if (!arg || typeof arg !== "object" || Array.isArray(arg)) {
    opts = { PIXIRef: arg || PIXI, app: null, pxPerMeter: undefined };
  } else {
    opts = arg;
  }

  const PIXIRef = opts.PIXIRef || PIXI;
  const appRef = opts.app || null;
  let basePxPerMeter =
    typeof opts.pxPerMeter === "number" && isFinite(opts.pxPerMeter)
      ? opts.pxPerMeter
      : undefined;

  let atlas = null;
  let unified = null;

  function ensureUnified() {
    if (!createUnifiedEmitterRuntime) return null;
    if (unified) return unified;

    unified = createUnifiedEmitterRuntime({
      PIXIRef,
      app: appRef,
      autorun: false,
      pxPerMeter: basePxPerMeter,
      parentLayer: appRef ? appRef.stage : undefined,
      aboveLayer: appRef ? appRef.stage : undefined,
      usePresetRulesOnly: true,
    });

    return unified;
  }

  async function ensureAtlasLoaded() {
    if (!ensureUnified()) return null;

    if (!atlas) {
      try {
        atlas = typeof getEmitterAtlas === "function" ? getEmitterAtlas() : null;
      } catch {
        atlas = null;
      }
    }

    if (!atlas) {
      try {
        const maybe =
          loadEmitterAtlas.length > 0
            ? await loadEmitterAtlas(PIXIRef)
            : await loadEmitterAtlas();
        atlas =
          maybe ||
          (typeof getEmitterAtlas === "function"
            ? getEmitterAtlas()
            : null) ||
          atlas;
      } catch (e) {
        console.warn(
          "[RuntimeBridge] loadEmitterAtlas failed; trying direct spritesheet load",
          e
        );
      }
    }

    if (!atlas) {
      const jsonUrl = "/assets/emitter/spritesheet.json";
      try {
        const res = await fetch(jsonUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const basePath = new URL(jsonUrl, window.location.origin)
          .toString()
          .replace(/[^/]+$/, "");
        const imageUrl = data?.meta?.image
          ? basePath + data.meta.image
          : "/assets/emitter/spritesheet.png";

        const tex = PIXIRef.Texture.from(imageUrl);
        const sheet = new PIXIRef.Spritesheet(tex.baseTexture, data);
        await sheet.parse();

        atlas = {
          spritesheet: sheet,
          textures: sheet.textures,
          frames: Object.keys(sheet.textures),
        };
      } catch (e) {
        console.error("[RuntimeBridge] Fallback spritesheet load failed:", e);
      }
    }

    if (atlas && unified && typeof unified.setTextures === "function") {
      try {
        unified.setTextures(atlas.textures || {});
      } catch (e) {
        console.warn("[RuntimeBridge] unified.setTextures failed:", e);
      }
    }

    return atlas;
  }

  async function runPreset(preset, fromImg, toImg, rootContainer, helpers) {
    if (!preset) return;

    const rt = ensureUnified();
    if (!rt) return;

    await ensureAtlasLoaded();

    // ──────────────────────────────────────────────
    // NORMALISE GLOW SETTINGS (EmitterTool → VTT)
    // ──────────────────────────────────────────────
    if (preset) {
      preset.glow = preset.glow || {};

      if ("glowEnabled" in preset) {
        preset.glow.enabled = !!preset.glowEnabled;
      }

      if ("glowPercent" in preset) {
        const p = Number(preset.glowPercent) || 0;
        preset.glow.percent01 = Math.max(0, Math.min(1, p / 100));
      }

      if ("glowIntensity" in preset) {
        preset.glow.intensity = Number(preset.glowIntensity) || 0;
      }

      if ("glowReachM" in preset) {
        preset.glow.reachM = Number(preset.glowReachM) || 0;
      }

      if ("glowDurationS" in preset) {
        preset.glow.durationS = Number(preset.glowDurationS) || 0.75;
      }
    }

    const parentLayer =
      rootContainer || (appRef ? appRef.stage : getFallbackStage());
    const aboveLayer = parentLayer;

    const h = helpers || {};
    const helperPPM =
      typeof h.pxPerMeter === "number" && isFinite(h.pxPerMeter)
        ? h.pxPerMeter
        : typeof h.mpp === "number" && isFinite(h.mpp)
        ? h.mpp
        : basePxPerMeter;

    const mergedHelpers = {
      ...h,
      mpp: helperPPM,
      pxPerMeter: helperPPM,
      toImg,
    };

    try {
      console.log("[RuntimeBridge] runPreset", {
        name: preset?.name || preset?.effectKey,
        fromImg,
        toImg,
        pxPerMeter: mergedHelpers.pxPerMeter,
        textures:
          atlas && atlas.textures ? Object.keys(atlas.textures).length : 0,
      });
      rt.runPreset(preset, fromImg, parentLayer, aboveLayer, mergedHelpers);
    } catch (e) {
      console.error("[RuntimeBridge] unified.runPreset error:", e);
    }
  }

  function getFallbackStage() {
    try {
      return PIXIRef.Ticker.shared._head?.context?.stage || undefined;
    } catch {
      return undefined;
    }
  }

  function update(dt) {
    const rt = ensureUnified();
    if (!rt || typeof rt.update !== "function") return;
    try {
      rt.update(dt);
    } catch (e) {
      console.error("[RuntimeBridge] update error:", e);
    }
  }

  function destroy() {
    const rt = unified;
    unified = null;
    if (rt && typeof rt.destroy === "function") {
      try {
        rt.destroy();
      } catch {}
    }
    atlas = null;
  }

  function setEnvironment(env = {}) {
    const rt = ensureUnified();
    if (!rt || typeof rt.setEnvironment !== "function") return;
    try {
      if (typeof env.pxPerMeter === "number" && isFinite(env.pxPerMeter)) {
        basePxPerMeter = env.pxPerMeter;
      }
      rt.setEnvironment(env);
    } catch (e) {
      console.warn("[RuntimeBridge] setEnvironment failed:", e);
    }

    broadcastWallsFromEnv(env);
  }

  return {
    runPreset,
    update,
    destroy,
    setEnvironment,
  };
}

export default { createEmitterRuntime };
